import { useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { KeyRound, Upload, Loader2, Search, Plus, Pencil, Trash2, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const empty = { negozio: "", rif_ordine: "", codice_assistenza: "", azienda: "", tipo_licenza: "" };

export function SoftwareLicensesCard() {
  const inputRef = useRef(null);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [dialog, setDialog] = useState({ open: false, editing: null, form: empty });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/software-licenses", { params: debouncedQ ? { q: debouncedQ } : {} });
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [debouncedQ]);

  useEffect(() => { load(); }, [load]);

  const handleImport = async (file) => {
    if (!file) return;
    setImportBusy(true);
    setImportResult(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/import/software-licenses", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      toast.success(`Import completato: ${data.inserted} inserite, ${data.updated} aggiornate`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally {
      setImportBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const openNew = () => setDialog({ open: true, editing: null, form: empty });
  const openEdit = (lic) => setDialog({ open: true, editing: lic, form: { ...empty, ...lic } });

  const save = async () => {
    const f = dialog.form;
    if (!f.negozio.trim() || !f.codice_assistenza.trim()) {
      toast.error("Negozio e Codice Assistenza sono obbligatori");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...f, rif_ordine: f.rif_ordine || null, azienda: f.azienda || null, tipo_licenza: f.tipo_licenza || null };
      if (dialog.editing) await api.put(`/software-licenses/${dialog.editing.id}`, payload);
      else await api.post("/software-licenses", payload);
      toast.success(dialog.editing ? "Licenza aggiornata" : "Licenza creata");
      setDialog({ open: false, editing: null, form: empty });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (lic) => {
    if (!window.confirm(`Eliminare la licenza ${lic.codice_assistenza} (${lic.negozio})?`)) return;
    try {
      await api.delete(`/software-licenses/${lic.id}`);
      toast.success("Licenza eliminata");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Card className="rounded-sm border-zinc-200 bg-white p-6" data-testid="settings-software-licenses">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-10 w-10 rounded-sm bg-violet-100 text-violet-700 flex items-center justify-center">
          <KeyRound className="h-5 w-5" strokeWidth={1.75} />
        </div>
        <div className="flex-1">
          <h3 className="font-display text-lg font-semibold tracking-tight text-zinc-900">Licenze software WineIdea</h3>
          <p className="text-sm text-zinc-600">Anagrafica licenze del gestionale WineIdea installato sui dispenser SELF.</p>
        </div>
      </div>

      <div className="rounded-sm border border-zinc-200 bg-zinc-50 p-4 mb-4">
        <div className="flex items-center gap-2 mb-2">
          <FileSpreadsheet className="h-4 w-4 text-zinc-600" />
          <div className="text-xs uppercase tracking-widest text-zinc-600 font-semibold">Import da Excel</div>
        </div>
        <p className="text-xs text-zinc-600 mb-3">
          File XLSX con colonne <b>NEGOZIO</b>, <b>RIF_ORDINE</b>, <b>CODICE_ASSISTENZA</b>, <b>AZIENDA</b>, <b>TIPO_LICENZA</b>. Chiave univoca = CODICE_ASSISTENZA.
        </p>
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx"
          className="hidden"
          data-testid="import-software-licenses-input"
          onChange={(e) => handleImport(e.target.files?.[0])}
        />
        <Button
          data-testid="import-software-licenses-btn"
          onClick={() => inputRef.current?.click()}
          disabled={importBusy}
          className="bg-brand hover:bg-brand-dark text-white rounded-sm"
        >
          {importBusy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Importazione...</> : <><Upload className="h-4 w-4 mr-2" /> Carica file</>}
        </Button>
        {importResult && (
          <div className="mt-3 rounded-sm border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900" data-testid="import-software-licenses-result">
            <div><b>{importResult.inserted}</b> inserite · <b>{importResult.updated}</b> aggiornate · {importResult.skipped} ignorate</div>
            {importResult.errors?.length > 0 && (
              <div className="text-amber-800 mt-1">{importResult.errors.length} errori (prime {Math.min(3, importResult.errors.length)}):
                <ul className="mt-1 list-disc list-inside">
                  {importResult.errors.slice(0, 3).map((er, i) => <li key={`err-${i}-${(er.error||'').slice(0,20)}`}>{er.row ? `Riga ${er.row}: ` : ""}{er.error}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center gap-3 mb-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-400" />
          <Input
            data-testid="software-licenses-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca per negozio, azienda o codice..."
            className="pl-9 h-8 rounded-sm text-sm"
          />
        </div>
        <Button data-testid="new-license-btn" onClick={openNew} size="sm" className="bg-brand hover:bg-brand-dark text-white rounded-sm">
          <Plus className="h-3.5 w-3.5 mr-1" /> Nuova licenza
        </Button>
      </div>

      <div className="rounded-sm border border-zinc-200 overflow-hidden">
        <div className="overflow-x-auto max-h-96 overflow-y-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-zinc-50 sticky top-0">
            <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-200">
              <th className="text-left px-4 py-2 font-medium">Negozio</th>
              <th className="text-left px-4 py-2 font-medium">Azienda</th>
              <th className="text-left px-4 py-2 font-medium">Codice</th>
              <th className="text-left px-4 py-2 font-medium">Ordine</th>
              <th className="text-left px-4 py-2 font-medium">Tipo</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="p-6 text-center text-zinc-500 text-xs"><Loader2 className="inline h-3 w-3 mr-1 animate-spin" /> Caricamento...</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-zinc-500 text-xs">Nessuna licenza. Importa da Excel o crea la prima manualmente.</td></tr>}
            {!loading && items.map((lic) => (
              <tr key={lic.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`lic-row-${lic.id}`}>
                <td className="px-4 py-2 text-zinc-900">{lic.negozio}</td>
                <td className="px-4 py-2 text-zinc-700">{lic.azienda || "—"}</td>
                <td className="px-4 py-2 text-violet-800 font-mono text-xs">{lic.codice_assistenza}</td>
                <td className="px-4 py-2 text-zinc-500 font-mono text-xs">{lic.rif_ordine || "—"}</td>
                <td className="px-4 py-2 text-zinc-700 text-xs">{lic.tipo_licenza || "—"}</td>
                <td className="px-4 py-2 text-right">
                  <div className="inline-flex gap-1">
                    <Button variant="outline" size="sm" className="h-7 rounded-sm px-2" data-testid={`lic-edit-${lic.id}`} onClick={() => openEdit(lic)}>
                      <Pencil className="h-3 w-3" />
                    </Button>
                    <Button variant="outline" size="sm" className="h-7 rounded-sm px-2 text-red-700 border-red-200 hover:bg-red-50" data-testid={`lic-del-${lic.id}`} onClick={() => remove(lic)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      <Dialog open={dialog.open} onOpenChange={(v) => !v && setDialog({ open: false, editing: null, form: empty })}>
        <DialogContent className="rounded-sm" data-testid="license-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">{dialog.editing ? "Modifica licenza" : "Nuova licenza"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Negozio *</Label>
              <Input data-testid="lic-negozio" value={dialog.form.negozio} onChange={(e) => setDialog((d) => ({ ...d, form: { ...d.form, negozio: e.target.value } }))} className="mt-1 rounded-sm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Codice Assistenza *</Label>
                <Input data-testid="lic-codice" value={dialog.form.codice_assistenza} onChange={(e) => setDialog((d) => ({ ...d, form: { ...d.form, codice_assistenza: e.target.value } }))} className="mt-1 rounded-sm font-mono" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Riferimento Ordine</Label>
                <Input data-testid="lic-ordine" value={dialog.form.rif_ordine} onChange={(e) => setDialog((d) => ({ ...d, form: { ...d.form, rif_ordine: e.target.value } }))} className="mt-1 rounded-sm font-mono" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Azienda / Ragione sociale</Label>
              <Input data-testid="lic-azienda" value={dialog.form.azienda} onChange={(e) => setDialog((d) => ({ ...d, form: { ...d.form, azienda: e.target.value } }))} className="mt-1 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Tipo licenza</Label>
              <Input data-testid="lic-tipo" value={dialog.form.tipo_licenza} onChange={(e) => setDialog((d) => ({ ...d, form: { ...d.form, tipo_licenza: e.target.value } }))} className="mt-1 rounded-sm" placeholder="es. WineIdea Pro, WineIdea Base" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setDialog({ open: false, editing: null, form: empty })}>Annulla</Button>
            <Button data-testid="lic-save" className="bg-brand hover:bg-brand-dark text-white rounded-sm" onClick={save} disabled={saving}>
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Salvataggio...</> : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
